import { Link, useLocation } from '@tanstack/react-router';
import { PieChart } from 'lucide-react';
import React, { Fragment, ReactNode } from 'react';

import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from 'ui/breadcrumb';
import { Separator } from 'ui/separator';

interface PageHeaderProps {
  title?: string | ReactNode;
  breadcrumbs: { label: ReactNode | string; path: string; search?: Record<string, unknown> }[];
  actions?: { renderActions: React.ReactNode };
}

const PageHeader: React.FC<PageHeaderProps> = ({ breadcrumbs = [], title, actions }) => {
  const location = useLocation();
  const activeCrumb = breadcrumbs[breadcrumbs.length - 1] || {};
  const crumbsExcludingActive = breadcrumbs.slice(0, -1);
  const breadcrumbReturnTo =
    typeof location.search.breadcrumbReturnTo === 'string' ? location.search.breadcrumbReturnTo : undefined;
  const breadcrumbReturnUrl = breadcrumbReturnTo ? new URL(breadcrumbReturnTo, window.location.origin) : undefined;
  const normalizePath = (path: string) => path.replace(/\/$/, '');

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <Link to={'/'}>
              <PieChart size={16} className="text-destructive-text" />
            </Link>
          </BreadcrumbItem>
          <BreadcrumbSeparator> / </BreadcrumbSeparator>
          {crumbsExcludingActive.map((item, index) => (
            <Fragment key={index}>
              <BreadcrumbItem>
                {breadcrumbReturnUrl && normalizePath(breadcrumbReturnUrl.pathname) === normalizePath(item.path) ? (
                  <Link
                    to={breadcrumbReturnUrl.pathname}
                    search={{ ...Object.fromEntries(breadcrumbReturnUrl.searchParams), ...item.search }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <Link to={item.path} search={item.search ?? ((search) => search)}>
                    {item.label}
                  </Link>
                )}
              </BreadcrumbItem>
              <BreadcrumbSeparator> / </BreadcrumbSeparator>
            </Fragment>
          ))}
          <BreadcrumbItem>
            <BreadcrumbPage>{activeCrumb.label}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex justify-between items-center mb-8">
        <div className="flex gap-2.5 items-center">
          <div className="flex h-8 gap-[1.5px]">
            <Separator orientation="vertical" variant="destructive" thickness="md" />
            <Separator orientation="vertical" variant="destructive" thickness="md" />
          </div>
          {typeof title === 'string' ? (
            <h4 className="font-bold text-sm md:text-base lg:text-page-title">{title}</h4>
          ) : (
            <div className="font-bold text-sm md:text-base lg:text-page-title">{title}</div>
          )}
        </div>
        <div> {actions?.renderActions}</div>
      </div>
    </div>
  );
};
export { PageHeader };
